import React from 'react';
import PropTypes from 'prop-types';
import PerfCascade, { Legend } from '@hydra/common/error/PerfCascade';

import {
  Grid,
  Link,
  Table,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Label, Translate, NotificationBanner } from '@hydra/common/components';

const ChartTitle = ({ url }) => <h5><Translate content='error_review_generation_box_request' /> {url}</h5>;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  bottomGutter: {
    marginBottom: theme.spacing(2)
  },
  tr: {
    '&:not(:first-child) td': {
      borderTop: `1px solid ${theme.palette.grey.light}`
    }
  },
  td: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '8px',
    lineHeight: '1.6',
    verticalAlign: 'top',
    '&:first-child': {
      wordBreak: 'break-word'
    }
  },
  table: {
    border: '0',
    width: '100%',
    tableLayout: 'auto',
    maxWidth: '100%',
    backgroundColor: 'rgba(0,0,0,0)',
    borderCollapse: 'collapse',
    borderSpacing: 0
  },
  label: {
    display: 'inherit',
    padding: theme.spacing(0, 0.5),
    borderRadius: theme.shape.borderRadius * 2
  },
  link: {
    fontSize: theme.typography.fontSizeSmall
  }
}));

const getResolveObject = (resolve = {}) => {
  const resolveObject = {};

  if (resolve.httpWwwHar) {
    resolveObject.httpWww = { ...resolve.httpWww, har: resolve.httpWwwHar };
  }

  if (resolve.httpsWwwHar) {
    resolveObject.httpsWww = { ...resolve.httpsWww, har: resolve.httpsWwwHar };
  }

  if (resolve.httpNonWwwHar) {
    resolveObject.httpNonWww = { ...resolve.httpNonWww, har: resolve.httpNonWwwHar };
  }

  if (resolve.httpsNonWwwHar) {
    resolveObject.httpsNonWww = { ...resolve.httpsNonWww, har: resolve.httpsNonWwwHar };
  }

  return resolveObject;
};

const ErrorHarGraph = ({ resolve }) => {
  const resolveObject = getResolveObject(resolve);
  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<Icon type='chevron-down' />}
        id='error-har-graph'
      >
        <Typography paragraph>
          <Translate content='error_review_generation_box_title' />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container justify='center' spacing={2}>
          <Grid item lg>
            <Legend />
            {
              Object.values(resolveObject).map(({ url, har }, index) => (
                <div key={index}>
                  <ChartTitle url={url} />
                  <PerfCascade doc={har} />
                </div>
              ))
            }
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const ErrorHarTable = ({ resolve }) => {
  const resolveObject = getResolveObject(resolve);
  const classes = useStyles();
  return (
    <Grid container justify='center' spacing={2}>
      <Typography paragraph>
        <Translate content='error_review_generation_content_3' />
      </Typography>
      <Grid container item xs={8} spacing={2} justify='center' className={classes.bottomGutter}>
        <Table className={classes.table}>
          <tbody>
            {
              Object.values(resolveObject).map(({ url, error, statusCode }, index) => (
                <tr key={index} className={classes.tr}>
                  <td className={classes.td}>
                    <Link
                      className={classes.link}
                      href={url}
                      rel='nofollow noreferrer noopener'
                      target='_blank'
                    >
                      {url}
                    </Link>
                  </td>
                  <td className={classes.td}>
                    <Label className={classes.label} style='error'>
                      {error ? error.code : statusCode}
                    </Label>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </Table>
      </Grid>
      <Grid container item xs={12} spacing={2} justify='center' className={classes.bottomGutter}>
        {resolve && <ErrorUniqueCodeLabels resolve={resolveObject} />}
      </Grid>
    </Grid>
  );
};

const getUniqueErrorCodeArray = (resolve = {}) => {
  const uniqueErrorCodeArray = Array.from(
    new Set(
      Object.values(resolve).filter(Boolean).map(
        ({ error = {}, statusCode }) => (error && error.code) || statusCode
      )
    )
  );
  return uniqueErrorCodeArray;
};

export const ErrorUniqueCodeLabels = ({ resolve }) => {
  const uniqueErrorCodes = getUniqueErrorCodeArray(resolve);
  const uniqueCodeLabels = uniqueErrorCodes.map(
    code => <Translate key={code} content={`error_review_generation_${code}`} />
  );
  return uniqueCodeLabels;
};

export const ErrorHar = ({ resolve }) => {
  const classes = useStyles();
  return (
    <Grid container justify='center' className={classes.root} spacing={2}>
      <Grid item xs={12}>
        <NotificationBanner
          variant='outlined'
          severity='error'
          title={<Translate content='error_review_generation_content_1' />}
        />
      </Grid>
      <Grid item xs={12}>
        <ErrorHarTable resolve={resolve} />
      </Grid>
      <Grid item xs={12}>
        <ErrorHarGraph resolve={resolve} />
      </Grid>
    </Grid>
  );
};

ErrorHar.propTypes = {
  resolve: PropTypes.shape({
    httpWww: PropTypes.object,
    httpWwwHar: PropTypes.object,
    httpNonWww: PropTypes.object,
    httpNonWwwHar: PropTypes.object,
    httpsWww: PropTypes.object,
    httpsWwwHar: PropTypes.object,
    httpsNonWww: PropTypes.object,
    httpsNonWwwHar: PropTypes.object
  })
};

export default ErrorHar;
